import {
  Add as AddIcon,
  ChevronRight,
  Group as GroupIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import { capitalizar, generarTemporadasAdyacentes, getInicialesNombre } from '../../utils/UtilesComun';
import {
  AvatarApp,
  BoxTitleApp,
  SelectWrapperApp,
  TypographyApp,
  VisibilidadIconApp,
} from '../custom/CustomComponents';

/**
 * Panel de listas de scouting con pestañas "Mías" y "De otros",
 * selector compacto de temporada y badges interactivos.
 */
const ListasUsuario = ({ todasListas = [], onUpdate = () => {}, initialTemporada = null, selectedIdLista = null }) => {
  const [tabActiva, setTabActiva] = useState(0);

  // Inicializar con la temporada de la lista activa, o con la temporada natural del año actual
  const [filtroTemporada, setFiltroTemporada] = useState(() => {
    if (initialTemporada) {
      return initialTemporada;
    }
    const anoActual = new Date().getFullYear();
    return `${anoActual}-${anoActual + 1}`;
  });

  // Sincronizar suavemente la temporada seleccionada sin provocar desmontaje del componente
  const [prevInitialTemporada, setPrevInitialTemporada] = useState(initialTemporada);
  if (initialTemporada && initialTemporada !== prevInitialTemporada) {
    setPrevInitialTemporada(initialTemporada);
    setFiltroTemporada(initialTemporada);
  }

  const [visibilidadAnchorEl, setVisibilidadAnchorEl] = useState(null);
  const [visibilidadListaId, setVisibilidadListaId] = useState(null);

  // Estado para creación de nueva lista
  const [crearModalOpen, setCrearModalOpen] = useState(false);
  const [nuevoNombreLista, setNuevoNombreLista] = useState('');
  const [nuevaTemporadaLista, setNuevaTemporadaLista] = useState(filtroTemporada);
  const [guardandoLista, setGuardandoLista] = useState(false);

  // Abrir modal de creación sincronizando la temporada por defecto
  const handleAbrirCrearModal = () => {
    setNuevaTemporadaLista(filtroTemporada);
    setNuevoNombreLista('');
    setCrearModalOpen(true);
  };

  // Mapa local para actualización optimista instantánea (0ms) de favoritos
  const [favoritasOverrides, setFavoritasOverrides] = useState({});

  const navigate = useNavigate();
  const { idUsuario, temporadaActual, modificarVisibilidadLista, toggleFavorita, crearLista } = useContext(Context);

  const handleVisibilidadMenuOpen = (event, idLista) => {
    event.stopPropagation();
    setVisibilidadAnchorEl(event.currentTarget);
    setVisibilidadListaId(idLista);
  };

  const handleVisibilidadMenuClose = () => {
    setVisibilidadAnchorEl(null);
    setVisibilidadListaId(null);
  };

  const handleVisibilidadChange = async (newVisibilidad) => {
    if (visibilidadListaId !== null) {
      const res = await modificarVisibilidadLista(visibilidadListaId, newVisibilidad);
      if (res && onUpdate) {
        onUpdate();
      }
    }
    handleVisibilidadMenuClose();
  };

  const handleConfirmarCrearLista = async () => {
    if (!nuevoNombreLista.trim() || guardandoLista) {
      return;
    }
    setGuardandoLista(true);
    const temporadaDestino = nuevaTemporadaLista || filtroTemporada;
    try {
      const res = await crearLista(nuevoNombreLista.trim(), idUsuario, temporadaDestino);
      if (res) {
        setFiltroTemporada(temporadaDestino);
        setNuevoNombreLista('');
        setCrearModalOpen(false);
        if (onUpdate) {
          await onUpdate();
        }
      }
    } catch (err) {
      console.error('Error al crear lista:', err);
    } finally {
      setGuardandoLista(false);
    }
  };

  // Determinar si una lista es favorita considerando overrides optimistas locales
  const getIsFavorita = useCallback(
    (lista) => {
      if (lista.idLista in favoritasOverrides) {
        return favoritasOverrides[lista.idLista];
      }
      return Boolean(lista.isFavorita ?? lista.favorita);
    },
    [favoritasOverrides]
  );

  const handleToggleFavorita = (event, idLista) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const lista = todasListas.find((l) => l.idLista === idLista);
    const estadoActual = lista ? getIsFavorita(lista) : false;
    const nuevoEstado = !estadoActual;

    // 1. Actualización optimista inmediata en 0ms
    setFavoritasOverrides((prev) => ({ ...prev, [idLista]: nuevoEstado }));

    // 2. Persistencia en servidor en segundo plano
    toggleFavorita(idLista)
      .then(() => {
        if (onUpdate) {
          onUpdate();
        }
      })
      .catch((err) => {
        console.error('Error al alternar favorita:', err);
        // Rollback en caso de error
        setFavoritasOverrides((prev) => {
          const next = { ...prev };
          delete next[idLista];
          return next;
        });
      });
  };

  const temporadasDisponibles = useMemo(() => {
    const baseTemps = new Set(generarTemporadasAdyacentes(temporadaActual));
    todasListas.forEach((l) => {
      if (l.temporada) {
        baseTemps.add(l.temporada);
      }
    });
    return Array.from(baseTemps).sort((a, b) => b.localeCompare(a));
  }, [todasListas, temporadaActual]);

  const todasFiltradas = useMemo(() => {
    return todasListas.filter((l) => l.temporada === filtroTemporada);
  }, [todasListas, filtroTemporada]);

  // Listas propias (Mías): favoritas siempre arriba, luego alfabéticamente
  const listasMias = useMemo(() => {
    const mias = todasFiltradas.filter((l) => l.idUsuario === idUsuario);
    return [...mias].sort((a, b) => {
      const favA = getIsFavorita(a);
      const favB = getIsFavorita(b);
      if (favA && !favB) {
        return -1;
      }
      if (!favA && favB) {
        return 1;
      }
      return (a.nombre || '').localeCompare(b.nombre || '');
    });
  }, [todasFiltradas, idUsuario, getIsFavorita]);

  // Listas ajenas (De otros): favoritas siempre arriba, luego alfabéticamente
  const listasOtros = useMemo(() => {
    const otros = todasFiltradas.filter((l) => l.idUsuario !== idUsuario);
    return [...otros].sort((a, b) => {
      const favA = getIsFavorita(a);
      const favB = getIsFavorita(b);
      if (favA && !favB) {
        return -1;
      }
      if (!favA && favB) {
        return 1;
      }
      return (a.nombre || '').localeCompare(b.nombre || '');
    });
  }, [todasFiltradas, idUsuario, getIsFavorita]);

  const listasAMostrar = tabActiva === 0 ? listasMias : listasOtros;

  return (
    <>
      {/* Título del panel con selector de temporada integrado y botón Crear */}
      <BoxTitleApp
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SelectWrapperApp
              value={filtroTemporada}
              onChange={(e) => setFiltroTemporada(e.target.value)}
              variant="standard"
              disableUnderline
              options={temporadasDisponibles}
              renderLabel={(temp) => `${temp}`}
              sx={{
                width: 'auto',
                minWidth: 85,
                height: 26,
                backgroundColor: 'transparent',
                border: 'none',
                '& .MuiSelect-select': {
                  py: 0.25,
                  px: 0.75,
                  fontSize: '0.80rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiSelect-icon': {
                  color: 'text.secondary',
                  fontSize: '1.05rem',
                  right: 0,
                },
                '& fieldset': {
                  border: 'none',
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '4px',
                },
              }}
            />
            <Tooltip title="Crear nueva lista" arrow placement="top">
              <Button
                size="small"
                onClick={handleAbrirCrearModal}
                startIcon={<AddIcon sx={{ fontSize: '0.95rem !important' }} />}
                sx={{
                  height: 26,
                  px: 1.25,
                  borderRadius: '13px',
                  backgroundColor: '#0f766e',
                  color: '#ffffff',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 1px 2px rgba(15, 118, 110, 0.2)',
                  '&:hover': {
                    backgroundColor: '#0d9488',
                    boxShadow: '0 2px 5px rgba(15, 118, 110, 0.3)',
                  },
                }}
              >
                Crear
              </Button>
            </Tooltip>
          </Box>
        }
      >
        Listas
      </BoxTitleApp>

      {/* Pestañas: Mías vs De otros */}
      <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 1 }}>
        <Tabs
          value={tabActiva}
          onChange={(e, nuevoValor) => setTabActiva(nuevoValor)}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 32,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.80rem',
              minHeight: 32,
              py: 0.4,
              px: 1,
            },
          }}
        >
          <Tab label={`Mías (${listasMias.length})`} />
          <Tab label={`De otros (${listasOtros.length})`} />
        </Tabs>
      </Box>

      {/* Listado de listas de la pestaña activa */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {listasAMostrar.length > 0 ? (
          listasAMostrar.map((lista) => {
            const esPropietario = lista.idUsuario === idUsuario;
            const isFav = getIsFavorita(lista);
            const isSelected =
              selectedIdLista !== null &&
              selectedIdLista !== undefined &&
              String(selectedIdLista) === String(lista.idLista);

            return (
              <Box
                key={lista.idLista}
                onClick={() => navigate(`/lista/${lista.idLista}`)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  minHeight: 36,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                  border: isSelected ? '1px solid #93c5fd' : '1px solid #f1f5f9',
                  boxShadow: isSelected ? '0 1px 3px rgba(37, 99, 235, 0.08)' : 'none',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    backgroundColor: isSelected ? '#dbeafe' : '#f8fafc',
                    borderColor: isSelected ? '#60a5fa' : '#e2e8f0',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                    '& .boton-ver-lista': {
                      backgroundColor: isSelected ? '#0284c7' : '#bae6fd',
                      borderColor: isSelected ? '#0369a1' : '#7dd3fc',
                      color: isSelected ? '#ffffff' : '#0284c7',
                      transform: 'translateY(-0.5px)',
                    },
                  },
                }}
              >
                {/* 1. Botón de favorita modernizado con respuesta inmediata */}
                <Tooltip title={isFav ? 'Quitar de favoritas' : 'Marcar como favorita'} arrow>
                  <Box
                    onClick={(e) => handleToggleFavorita(e, lista.idLista)}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      backgroundColor: isFav ? '#fef3c7' : 'transparent',
                      border: isFav ? '1px solid #fcd34d' : '1px solid transparent',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        backgroundColor: isFav ? '#fde68a' : '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    {isFav ? (
                      <StarIcon sx={{ color: '#d97706', fontSize: '1rem' }} />
                    ) : (
                      <StarBorderIcon sx={{ color: '#94a3b8', fontSize: '1rem' }} />
                    )}
                  </Box>
                </Tooltip>

                {/* 2. Nombre de la lista */}
                <Tooltip title={lista.nombre} arrow placement="top">
                  <TypographyApp
                    sx={{
                      flexGrow: 1,
                      fontSize: '0.78rem',
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? '#1d4ed8' : 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {lista.nombre}
                  </TypographyApp>
                </Tooltip>

                {/* 3. Visibilidad de la lista */}
                {esPropietario ? (
                  <Tooltip
                    title={`Visibilidad: ${capitalizar(lista.visibilidad || 'PRIVADA')} (clic para cambiar)`}
                    arrow
                  >
                    <Box
                      onClick={(e) => handleVisibilidadMenuOpen(e, lista.idLista)}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{
                        width: 22,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': { backgroundColor: '#e2e8f0' },
                      }}
                    >
                      <VisibilidadIconApp visibilidad={lista.visibilidad} sx={{ fontSize: '0.85rem' }} />
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={`Visibilidad: ${capitalizar(lista.visibilidad || 'PRIVADA')}`} arrow>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <VisibilidadIconApp
                        visibilidad={lista.visibilidad}
                        sx={{ fontSize: '0.85rem', color: 'text.secondary' }}
                      />
                    </Box>
                  </Tooltip>
                )}

                {/* 4. Creador de la lista */}
                {lista.nombreUsuario && (
                  <Tooltip title={`Creado por: ${lista.nombreUsuario}`} arrow>
                    <AvatarApp size={20} esPropio={esPropietario} sx={{ flexShrink: 0 }}>
                      {getInicialesNombre(lista.nombreUsuario)}
                    </AvatarApp>
                  </Tooltip>
                )}

                {/* 5. Badge de acción Ver › */}
                <Tooltip title="Ver lista y jugadores" arrow>
                  <Box
                    className="boton-ver-lista"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/lista/${lista.idLista}`);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px',
                      height: 22,
                      px: 0.75,
                      borderRadius: '5px',
                      fontSize: '0.70rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      flexShrink: 0,
                      backgroundColor: isSelected ? '#2563eb' : '#e0f2fe',
                      color: isSelected ? '#ffffff' : '#0369a1',
                      border: isSelected ? '1px solid #1d4ed8' : '1px solid #bae6fd',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.15s ease-in-out',
                    }}
                  >
                    <span>Ver</span>
                    <ChevronRight sx={{ fontSize: '0.80rem' }} />
                  </Box>
                </Tooltip>
              </Box>
            );
          })
        ) : (
          <Box sx={{ py: 3, px: 1, textAlign: 'center' }}>
            <TypographyApp sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              {tabActiva === 0
                ? 'No tienes listas creadas para esta temporada.'
                : 'No hay listas de otros usuarios en esta temporada.'}
            </TypographyApp>
          </Box>
        )}
      </Box>

      {/* Menú contextual de cambio de visibilidad para listas propias */}
      <Menu anchorEl={visibilidadAnchorEl} open={Boolean(visibilidadAnchorEl)} onClose={handleVisibilidadMenuClose}>
        <MenuItem onClick={() => handleVisibilidadChange('PRIVADA')} sx={{ fontSize: '0.8rem' }}>
          <LockIcon sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} /> Privada
        </MenuItem>
        <MenuItem onClick={() => handleVisibilidadChange('PUBLICA')} sx={{ fontSize: '0.8rem' }}>
          <PublicIcon sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} /> Pública
        </MenuItem>
        <MenuItem onClick={() => handleVisibilidadChange('COMPARTIDA')} sx={{ fontSize: '0.8rem' }}>
          <GroupIcon sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} /> Compartida
        </MenuItem>
      </Menu>

      {/* Modal para Crear Nueva Lista */}
      <Dialog
        open={crearModalOpen}
        onClose={() => {
          if (!guardandoLista) {
            setCrearModalOpen(false);
            setNuevoNombreLista('');
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 0.5,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>Crear nueva lista</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Nombre de la lista"
            placeholder="Ej. Promesas Infantil A"
            value={nuevoNombreLista}
            onChange={(e) => setNuevoNombreLista(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && nuevoNombreLista.trim()) {
                e.preventDefault();
                handleConfirmarCrearLista();
              }
            }}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&.Mui-focused fieldset': {
                  borderColor: '#0f766e',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#0f766e',
              },
            }}
          />
          <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.84rem', color: '#334155', fontWeight: 600 }}>Temporada asignada:</Typography>
            <SelectWrapperApp
              value={nuevaTemporadaLista}
              onChange={(e) => setNuevaTemporadaLista(e.target.value)}
              options={temporadasDisponibles}
              renderLabel={(temp) => `${temp}`}
              variant="outlined"
              size="small"
              sx={{
                width: 140,
                minWidth: 140,
                height: 34,
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: '#1e293b',
                p: 0,
                m: 0,
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 1.25,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiSelect-icon': {
                  color: '#64748b',
                  fontSize: '1.15rem',
                },
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                  borderColor: '#94a3b8',
                },
                '&.Mui-focused': {
                  borderColor: '#0f766e',
                },
                '& fieldset': {
                  border: 'none',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setCrearModalOpen(false);
              setNuevoNombreLista('');
            }}
            disabled={guardandoLista}
            sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.82rem' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!nuevoNombreLista.trim() || guardandoLista}
            onClick={handleConfirmarCrearLista}
            sx={{
              backgroundColor: '#0f766e',
              color: '#ffffff',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              '&:hover': { backgroundColor: '#0d9488' },
              '&.Mui-disabled': {
                backgroundColor: '#cbd5e1',
                color: '#94a3b8',
              },
            }}
          >
            {guardandoLista ? 'Creando...' : 'Crear lista'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ListasUsuario.propTypes = {
  todasListas: PropTypes.array,
  onUpdate: PropTypes.func,
  initialTemporada: PropTypes.string,
  selectedIdLista: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ListasUsuario;
